import React from "react";
import {Alert, Autocomplete, Button, Checkbox, IconButton, Menu, MenuItem, Popover, Skeleton, TextField, Tooltip} from "@mui/material";
import {EditOutlined, ViewColumnOutlined, WarningAmberOutlined} from "@mui/icons-material";
import {Tag} from "~/components/tag/Tag.tsx";
import {formatAmount, formatDate} from "../contractExplorerTypes.ts";
import type {ClLinkStatus, CompetingContractRef, ContractLinkRow, EntityRow, MatchStatusResponse} from "../useContractLinksQueries.ts";
import {LinkTransactionPicker} from "./LinkTransactionPicker.tsx";
import {SupplierMatchPicker} from "./SupplierMatchPicker.tsx";
import {useReviewContractMutation} from "../useReviewContractMutation.ts";
import type {ExtraPartColumn} from "../useContractLinksQueries.ts";
import {
    ACTIVE_LINK_STATUSES,
    honouredExtraColumns,
    useContractLinksQuery,
    useExtraPartColumns,
    useEntitiesQuery,
    useLinkActionMutation,
    useMatchStatusQuery,
    useRunMatchingMutation,
    useSetEntitiesMutation,
} from "../useContractLinksQueries.ts";

/** `linked` is the matcher's own decision; accepted/claimed carry a reviewer.
 *  The labels make that difference explicit in the Status column. */
const STATUS_TAG: Record<ClLinkStatus, {label: string; color: 'success' | 'info' | 'warning' | 'error' | 'default'}> = {
    linked: {label: 'Auto-linked', color: 'default'},
    accepted: {label: 'Accepted', color: 'success'},
    claimed: {label: 'Claimed', color: 'success'},
    candidate: {label: 'Candidate', color: 'warning'},
    competing: {label: 'Competing', color: 'info'},
    rejected: {label: 'Rejected', color: 'error'},
};

const PREREQ_LABEL = {not_found: 'not found in the contract', not_matched: 'not matched'};

/** Transaction, [Supplier], Date, Spend — then one column per selected extra,
 *  then the matching verdict (Confidence, Status, Reason) and Actions. The
 *  transaction's own facts come first; how it was matched reads last. Shared
 *  by every section so the tables line up as one grid whatever is selected.
 *  Supplier and the configured extras are OPTIONAL — off by default, toggled
 *  through the column picker; the six core columns are always shown. */
const VERDICT_COLUMN_WIDTHS = ['7%', '10%', '17%'];
const ACTIONS_WIDTH = '13%';
const EXTRA_COLUMN_WIDTH = '10%';
const SUPPLIER_WIDTH = '13%';

const columnWidths = (showSupplier: boolean, extraCount: number): string[] => {
    const optional = extraCount * 10 + (showSupplier ? 13 : 0);
    return [
        // Transaction and Date absorb whatever the optional columns free up
        `${Math.max(53 - optional - 24, 22)}%`,
        ...(showSupplier ? [SUPPLIER_WIDTH] : []),
        '9%',
        '9%',
        ...Array.from({length: extraCount}, () => EXTRA_COLUMN_WIDTH),
        ...VERDICT_COLUMN_WIDTHS,
        ACTIONS_WIDTH,
    ];
};

/** Remembered per browser: which optional columns the reviewer wants. */
const COLUMNS_STORAGE_KEY = 'contract-explorer.links-columns';

const loadColumnSelection = (): string[] => {
    try {
        const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter(v => typeof v === 'string') : [];
    } catch {
        return [];
    }
};

const saveColumnSelection = (keys: string[]) => {
    try {
        localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(keys));
    } catch {
        // storage may be unavailable; the selection just won't persist
    }
};

const SUPPLIER_COLUMN_KEY = 'supplier';

type Props = {
    bagId: number;
    contractId: number;
    currency: string;
    /** The contract's current version and effective date, so the date tile can
     *  edit the header through the same review path the Details tab uses. */
    versionNo: number;
    effectiveDate: string | null;
    /** Open another contract in this same modal (rewrites `?contract=`), so a
     *  competing contract is one click away instead of a search. */
    onOpenContract: (contractId: number) => void;
};

/**
 * The Transactions tab of the contract modal: matching prerequisites, the
 * linker's context, and the matches grouped by review state — with the
 * accept / reject / claim / manual-link actions.
 *
 * Candidates are deliberately a separate section from the linked transactions:
 * they are possible matches awaiting review, not owned spend.
 */
export const ContractLinksPanel: React.FC<Props> = (
    {bagId, contractId, currency, versionNo, effectiveDate, onOpenContract},
) => {
    const {data: status, isPending: statusPending} = useMatchStatusQuery(bagId, contractId);
    // Extra columns come from the databag's columnConfig, and only when the
    // customer profile opts in. They are all OFF by default: the reviewer
    // opts in through the column picker, and only the selected ones are even
    // requested from the API.
    const configuredColumns = useExtraPartColumns();
    const [selectedColumns, setSelectedColumns] = React.useState<string[]>(loadColumnSelection);
    const toggleColumn = (key: string) => setSelectedColumns(previous => {
        const next = previous.includes(key) ? previous.filter(k => k !== key) : [...previous, key];
        saveColumnSelection(next);
        return next;
    });
    const showSupplier = selectedColumns.includes(SUPPLIER_COLUMN_KEY);
    const selectedExtras = configuredColumns.filter(column => selectedColumns.includes(column.key));
    const {data: links, isPending: linksPending, isError, error} = useContractLinksQuery(
        bagId, contractId, selectedExtras.map(column => column.key),
    );
    const linkAction = useLinkActionMutation();
    const runMatching = useRunMatchingMutation();

    if (isError) return <Alert severity="error">Could not load the matches: {error?.message}</Alert>;
    if (statusPending || linksPending || !links || !status) {
        return <Skeleton variant="rectangular" height={260}/>;
    }

    const act = (row: ContractLinkRow, action: 'accept' | 'reject' | 'claim') =>
        linkAction.mutate({bagId, contractId, linkId: row.id, action});

    const rows = links.results;
    // The server's allowlist wins: render headers only for what it honoured
    // (read off the rows' `extra` keys — the page envelope does not say).
    const extraColumns = honouredExtraColumns(selectedExtras, rows);
    const active = rows.filter(r => (ACTIVE_LINK_STATUSES as string[]).includes(r.status));
    const candidates = rows.filter(r => r.status === 'candidate');
    const competing = rows.filter(r => r.status === 'competing');
    const rejected = rows.filter(r => r.status === 'rejected');
    const actionError = linkAction.error ?? runMatching.error;

    return (
        <div className="cl-panel">
            <MatchContextBar bagId={bagId} contractId={contractId} status={status}
                             versionNo={versionNo} effectiveDate={effectiveDate}/>

            <div className="cl-run-row">
                {status.context?.ai_summary && (
                    <p className="cl-summary">{status.context.ai_summary}</p>
                )}
                <ColumnPicker
                    configured={configuredColumns}
                    selected={selectedColumns}
                    onToggle={toggleColumn}
                />
                {status.ready_for_matching && (
                    <Tooltip title={status.context?.matched_at
                        ? 'Run the matcher again — proposals are refreshed, your reviewed rows stay'
                        : 'Search this supplier\u2019s transactions for the ones this contract covers'}>
                        <span>
                            <Button
                                variant={status.context?.matched_at ? 'outlined' : 'contained'}
                                size="small"
                                disabled={runMatching.isPending}
                                onClick={() => runMatching.mutate({bagId, contractId})}>
                                {runMatching.isPending
                                    ? 'Matching\u2026'
                                    : status.context?.matched_at ? 'Re-run matching' : 'Find matching transactions'}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </div>
            {actionError && <Alert severity="error" className="cl-alert">{actionError.message}</Alert>}

            <Section
                title={`Linked transactions (${active.length})`}
                hint="Owned by this contract — accept an auto-link to mark it reviewed"
                empty="No transactions are linked to this contract yet."
                rows={active}
                currency={currency}
                extraColumns={extraColumns}
                showSupplier={showSupplier}
                renderExtra={row => row.competing_contracts.length ? (
                    <RelatedContractsMenu
                        label={`Competing contracts: ${row.competing_contracts.length}`}
                        tooltip="Show the contracts that also matched this transaction"
                        className="cl-competing-badge"
                        contracts={row.competing_contracts}
                        onOpenContract={onOpenContract}
                    />
                ) : null}
                renderActions={row => (
                    <>
                        {row.status === 'linked' && (
                            <Tooltip title="Confirm this automatic link as reviewed">
                                <Button size="small" disabled={linkAction.isPending}
                                        onClick={() => act(row, 'accept')}>
                                    Accept
                                </Button>
                            </Tooltip>
                        )}
                        <Button size="small" color="error" disabled={linkAction.isPending}
                                onClick={() => act(row, 'reject')}>
                            Reject
                        </Button>
                    </>
                )}
            />

            <Section
                title={`Candidates (${candidates.length})`}
                hint="Below the confidence threshold — not linked until you accept them"
                empty="No candidate matches."
                rows={candidates}
                currency={currency}
                extraColumns={extraColumns}
                showSupplier={showSupplier}
                renderActions={row => (
                    <>
                        <Button size="small" disabled={linkAction.isPending} onClick={() => act(row, 'accept')}>
                            Accept
                        </Button>
                        <Button size="small" color="error" disabled={linkAction.isPending}
                                onClick={() => act(row, 'reject')}>
                            Reject
                        </Button>
                    </>
                )}
            />

            <Section
                title={`Competing (${competing.length})`}
                hint="Qualified here too, but another contract owns the transaction"
                empty="No competing matches."
                rows={competing}
                currency={currency}
                extraColumns={extraColumns}
                showSupplier={showSupplier}
                renderExtra={row => row.owned_by ? (
                    <RelatedContractsMenu
                        label={`Linked to ${row.owned_by.contract_title || `contract ${row.owned_by.contract_id}`}`}
                        tooltip="Show the contract that owns this transaction"
                        className="cl-owner-badge"
                        contracts={[row.owned_by]}
                        onOpenContract={onOpenContract}
                    />
                ) : null}
                renderActions={row => (
                    <Tooltip title="Take this transaction from the contract that owns it">
                        <Button size="small" disabled={linkAction.isPending} onClick={() => act(row, 'claim')}>
                            Claim
                        </Button>
                    </Tooltip>
                )}
            />

            {rejected.length > 0 && (
                <Section
                    title={`Rejected (${rejected.length})`}
                    hint="Vetoed by a reviewer — not counted as linked, but you can put one back"
                    empty=""
                    rows={rejected}
                    currency={currency}
                    extraColumns={extraColumns}
                    showSupplier={showSupplier}
                    muted
                    renderActions={row => (
                        <Tooltip title="Undo the rejection and link this transaction to the contract">
                            <Button size="small" disabled={linkAction.isPending} onClick={() => act(row, 'accept')}>
                                Accept
                            </Button>
                        </Tooltip>
                    )}
                />
            )}

            <div className="cl-manual">
                <LinkTransactionPicker
                    bagId={bagId}
                    contractId={contractId}
                    matchedSupplierName={status.supplier.matched_supplier?.name
                        ?? status.supplier.matched_parent_supplier?.name}
                />
                <span className="cl-manual-hint">
                    Link a transaction the matcher did not propose.
                </span>
            </div>
        </div>
    );
};

/** The other contracts involved with a transaction, as a menu.
 *
 *  Always a menu, even for a single entry: the click target says how many
 *  there are, not which one, so opening a contract straight from it would
 *  navigate before the user has seen what they were choosing. Used on both
 *  sides — the competitors of a row this contract owns, and the owner of a row
 *  it is competing for. */
const RelatedContractsMenu: React.FC<{
    label: string;
    tooltip: string;
    className: string;
    contracts: CompetingContractRef[];
    onOpenContract: (contractId: number) => void;
}> = ({label, tooltip, className, contracts, onOpenContract}) => {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);

    return (
        <>
            <Tooltip title={tooltip}>
                <button type="button" className={className} onClick={e => setAnchor(e.currentTarget)}>
                    {label}
                </button>
            </Tooltip>
            <Menu open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}>
                {contracts.map(contract => (
                    <MenuItem
                        key={contract.contract_id}
                        onClick={() => {
                            setAnchor(null);
                            onOpenContract(contract.contract_id);
                        }}>
                        {contract.contract_title || `Contract ${contract.contract_id}`}
                        {contract.confidence !== null && ` · ${contract.confidence}`}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

/** One prerequisite as a fixed-size tile, so the header keeps the same
 *  footprint whether everything is matched or nothing is — problems are an
 *  indicator on the tile, not a bigger layout. */
const Tile: React.FC<{
    label: string;
    problem: boolean;
    value: React.ReactNode;
    sub?: React.ReactNode;
    action?: React.ReactNode;
}> = ({label, problem, value, sub, action}) => (
    <div className={problem ? 'cl-tile is-problem' : 'cl-tile'}>
        <span className="cl-tile-label">
            {label}
            {problem && <WarningAmberOutlined className="cl-tile-warn" fontSize="inherit"/>}
        </span>
        <span className="cl-tile-value">{value}</span>
        {sub && <span className="cl-tile-sub">{sub}</span>}
        {action && <span className="cl-tile-action">{action}</span>}
    </div>
);

const EDIT_TRIGGER_TITLE = {matched: 'Change', unmatched: 'Resolve'};

const MatchContextBar: React.FC<{
    bagId: number;
    contractId: number;
    status: MatchStatusResponse;
    versionNo: number;
    effectiveDate: string | null;
}> = ({bagId, contractId, status, versionNo, effectiveDate}) => {
    const [entityAnchor, setEntityAnchor] = React.useState<HTMLElement | null>(null);
    const {data: entities} = useEntitiesQuery(bagId, Boolean(entityAnchor));
    const setEntities = useSetEntitiesMutation();
    // Preselected with the current assignment: the popover edits the full set.
    const [picked, setPicked] = React.useState<EntityRow[]>([]);
    const openEntityPopover = (event: React.MouseEvent<HTMLElement>) => {
        setPicked(status.entity.entities);
        setEntityAnchor(event.currentTarget);
    };
    const [dateAnchor, setDateAnchor] = React.useState<HTMLElement | null>(null);
    const [dateDraft, setDateDraft] = React.useState(effectiveDate?.slice(0, 10) ?? '');
    const reviewContract = useReviewContractMutation();

    const supplierMatched = status.supplier.status === 'matched';
    const entityMatched = status.entity.status === 'matched';
    const dateFound = status.effective_date.status === 'found';
    const supplierName = status.supplier.matched_supplier?.name
        ?? status.supplier.matched_parent_supplier?.name;

    /** The parsed name is what a reviewer checks the match against, so it gets
     *  a labelled line of its own instead of grey prose in parentheses. */
    const parsedLine = (value: string | null) => (
        <>
            <span className="cl-tile-sub-label">Parsed</span>
            <span className="cl-tile-parsed" title={value || undefined}>{value || 'not extracted'}</span>
        </>
    );

    const closeEntity = () => {
        setEntityAnchor(null);
        setPicked([]);
    };

    return (
        <>
            <div className="cl-context-bar">
                <Tile
                    label="Supplier"
                    problem={!supplierMatched}
                    value={supplierMatched
                        ? supplierName
                        : PREREQ_LABEL[status.supplier.status as keyof typeof PREREQ_LABEL]}
                    sub={
                        <>
                            {parsedLine(status.supplier.value)}
                            {supplierMatched && (
                                <span className="cl-tile-level">{status.supplier.match_level} level</span>
                            )}
                        </>
                    }
                    action={
                        <SupplierMatchPicker
                            bagId={bagId}
                            contractId={contractId}
                            contractSupplierName={status.supplier.value ?? ''}
                            renderTrigger={open => (
                                <Tooltip title={`${EDIT_TRIGGER_TITLE[supplierMatched ? 'matched' : 'unmatched']} the supplier match`}>
                                    <IconButton size="small" onClick={open}>
                                        <EditOutlined fontSize="inherit"/>
                                    </IconButton>
                                </Tooltip>
                            )}
                        />
                    }
                />

                <Tile
                    label={status.entity.entities.length > 1 ? 'Entities' : 'Entity'}
                    problem={!entityMatched}
                    value={entityMatched
                        ? (
                            <Tooltip title={status.entity.entities.map(e => e.name).join(', ')}>
                                <span>
                                    {status.entity.entities[0]?.name}
                                    {status.entity.entities.length > 1 &&
                                        ` +${status.entity.entities.length - 1}`}
                                </span>
                            </Tooltip>
                        )
                        : PREREQ_LABEL[status.entity.status as keyof typeof PREREQ_LABEL]}
                    sub={parsedLine(status.entity.value.join('; '))}
                    action={
                        <Tooltip title={`${EDIT_TRIGGER_TITLE[entityMatched ? 'matched' : 'unmatched']} the entities`}>
                            <IconButton size="small" onClick={openEntityPopover}>
                                <EditOutlined fontSize="inherit"/>
                            </IconButton>
                        </Tooltip>
                    }
                />

                <Tile
                    label="Effective date"
                    problem={!dateFound}
                    value={dateFound ? formatDate(status.effective_date.value) : 'not found'}
                    sub={<span className="cl-tile-sub-label">from the contract header</span>}
                    action={
                        <Tooltip title={dateFound ? 'Change the effective date' : 'Set the effective date'}>
                            <IconButton size="small" onClick={e => setDateAnchor(e.currentTarget)}>
                                <EditOutlined fontSize="inherit"/>
                            </IconButton>
                        </Tooltip>
                    }
                />
            </div>
            {!status.ready_for_matching && (
                <p className="cl-context-note">
                    Matching cannot run for this contract until the highlighted fields are resolved.
                </p>
            )}

            <Popover
                open={Boolean(dateAnchor)}
                anchorEl={dateAnchor}
                onClose={() => setDateAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}>
                <div className="cl-date-popover">
                    <TextField
                        size="small"
                        type="date"
                        autoFocus
                        label="Effective date"
                        slotProps={{inputLabel: {shrink: true}}}
                        value={dateDraft}
                        onChange={e => setDateDraft(e.target.value)}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        disabled={reviewContract.isPending || dateDraft === (effectiveDate?.slice(0, 10) ?? '')}
                        // The date is contract data, not a matching setting, so
                        // saving it here appends a user version exactly like the
                        // Details tab's form does.
                        onClick={() => reviewContract.mutate({
                            bagId,
                            contractId,
                            expectedVersionNo: versionNo,
                            header: {effective_date: dateDraft || null},
                        }, {onSuccess: () => setDateAnchor(null)})}>
                        Save
                    </Button>
                </div>
            </Popover>

            <Popover
                open={Boolean(entityAnchor)}
                anchorEl={entityAnchor}
                onClose={closeEntity}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}>
                <div className="cl-entity-popover">
                    <Autocomplete
                        size="small"
                        multiple
                        options={entities ?? []}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={picked}
                        onChange={(_e, value) => setPicked(value)}
                        // Entity names are long company names; the dropdown must
                        // size to them instead of inheriting the input's width.
                        slotProps={{popper: {style: {width: 'fit-content', minWidth: 340}, placement: 'bottom-start'}}}
                        renderInput={params => <TextField {...params} autoFocus label="Canonical entities"/>}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        disabled={setEntities.isPending}
                        onClick={() => setEntities.mutate(
                            {bagId, contractId, entityIds: picked.map(entity => entity.id)},
                            {onSuccess: closeEntity},
                        )}>
                        {picked.length ? `Use ${picked.length} ${picked.length === 1 ? 'entity' : 'entities'}` : 'Clear selection'}
                    </Button>
                </div>
            </Popover>
        </>
    );
};

/** Optional-column toggle: Supplier plus whatever part columns the databag's
 *  columnConfig offers. The six core columns are not listed — they always show. */
const ColumnPicker: React.FC<{
    configured: ExtraPartColumn[];
    selected: string[];
    onToggle: (key: string) => void;
}> = ({configured, selected, onToggle}) => {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    const options = [
        {key: SUPPLIER_COLUMN_KEY, label: 'Supplier'},
        ...configured.map(column => ({key: column.key, label: column.label})),
    ];
    return (
        <>
            <Tooltip title="Choose optional columns">
                <IconButton size="small" className="cl-column-picker-button"
                            onClick={e => setAnchor(e.currentTarget)}>
                    <ViewColumnOutlined fontSize="small"/>
                </IconButton>
            </Tooltip>
            <Menu open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}>
                {options.map(option => (
                    <MenuItem key={option.key} dense onClick={() => onToggle(option.key)}>
                        <Checkbox size="small" checked={selected.includes(option.key)} disableRipple/>
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};


const Section: React.FC<{
    title: string;
    hint: string;
    empty: string;
    rows: ContractLinkRow[];
    currency: string;
    extraColumns: ExtraPartColumn[];
    showSupplier: boolean;
    muted?: boolean;
    renderExtra?: (row: ContractLinkRow) => React.ReactNode;
    renderActions?: (row: ContractLinkRow) => React.ReactNode;
}> = ({title, hint, empty, rows, currency, extraColumns, showSupplier, muted, renderExtra, renderActions}) => (
    <section className={muted ? 'cl-section cl-muted' : 'cl-section'}>
        <h3 className="cl-section-title">
            {title}
            <span className="cl-section-hint">{hint}</span>
        </h3>
        {rows.length === 0
            ? (empty && <p className="cd-empty">{empty}</p>)
            : (
                <div className="cd-table-scroll">
                    {/* Each section is its own table, so the columns would size
                        independently and the sections would not line up. The
                        shared colgroup plus `table-layout: fixed` gives them
                        one geometry. The actions column is always rendered,
                        empty when a section has no actions, for the same
                        reason. */}
                    <table className="cd-table cl-table">
                        <colgroup>
                            {columnWidths(showSupplier, extraColumns.length).map((width, index) =>
                                <col key={`${index}-${width}`} style={{width}}/>)}
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Transaction</th>
                                {showSupplier && <th>Supplier</th>}
                                <th>Date</th>
                                <th className="cd-num">Spend ({currency})</th>
                                {extraColumns.map(column => <th key={column.key}>{column.label}</th>)}
                                <th className="cd-num">Confidence</th>
                                <th>Status</th>
                                <th>Reason</th>
                                <th aria-label="Actions"/>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row.id}>
                                    <td>
                                        <span className="cl-desc">{row.description || row.p_id}</span>
                                        {row.description && <span className="cd-sub">{row.p_id}</span>}
                                        {renderExtra?.(row)}
                                    </td>
                                    {showSupplier && <td className="cl-supplier">{row.supplier || '—'}</td>}
                                    <td>{formatDate(row.date)}</td>
                                    <td className="cd-num">{formatAmount(row.spend)}</td>
                                    {extraColumns.map(column => (
                                        <td key={column.key} className="cl-extra"
                                            title={String(row.extra?.[column.key] ?? '')}>
                                            {row.extra?.[column.key] ?? '—'}
                                        </td>
                                    ))}
                                    <td className="cd-num">{row.confidence ?? '—'}</td>
                                    <td>
                                        <Tooltip title={row.acted_by
                                            ? `Reviewed by ${row.acted_by}`
                                            : 'Assigned by the matcher — not reviewed yet'}>
                                            <span>
                                                <Tag label={STATUS_TAG[row.status].label}
                                                     color={STATUS_TAG[row.status].color}/>
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td className="cl-reason">{row.match_reason || '—'}</td>
                                    <td className="cl-actions">{renderActions?.(row)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
    </section>
);
